import { apiFetch } from "./auth";

/* Conversion taxonomy. These exact names reach GTM (dataLayer event), GA4 and our own crm_conversions table (no personal data: event, parameters, landing page, referrer, UTM, random session id, time). */
export const CONVERSIONS = ["eligibility_start", "eligibility_complete", "lead_capture", "checklist_download", "demo_request", "briefing_subscribe", "consultant_enquiry", "whatsapp_click", "call_click", "apply_start", "apply_complete"];

const tags = () => window.__vlTags || {};

/* Random per-tab session id: the only identifier stored with a conversion (no visitor id, no account). */
const sessionId = () => {
  try {
    let s = sessionStorage.getItem("vl-sid");
    if (!s) { s = `s_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`; sessionStorage.setItem("vl-sid", s); }
    return s;
  } catch { return null; }
};

const ENGINES = [[/google\./, "google"], [/bing\./, "bing"], [/duckduckgo/, "duckduckgo"], [/yahoo\./, "yahoo"], [/yandex\./, "yandex"], [/baidu\./, "baidu"], [/ecosia\./, "ecosia"]];
const SOCIAL = /facebook|instagram|linkedin|twitter|(^|\.)x\.com|t\.co$|youtube|whatsapp|reddit|pinterest|threads\.net|tiktok/;
const AI = /chatgpt|openai|perplexity|gemini\.google|claude\.ai|copilot\.microsoft/;

/* Session attribution: utm/gclid or referrer → source/medium, plus the landing page. Captured once at module load (the real landing URL) and kept for the tab's session. */
const ATTR = "vl-attr";
export function attribution() {
  try { const s = sessionStorage.getItem(ATTR); if (s) return JSON.parse(s); } catch { /* fresh */ }
  const q = new URLSearchParams(window.location.search);
  let ref = null; try { ref = document.referrer ? new URL(document.referrer) : null; } catch { ref = null; }
  const external = ref && ref.hostname !== window.location.hostname ? ref : null;
  let source = q.get("utm_source") || "", medium = q.get("utm_medium") || "";
  if (!source) {
    if (q.get("gclid") || q.get("gbraid") || q.get("wbraid")) { source = "google"; medium = "cpc"; }
    else if (q.get("msclkid")) { source = "bing"; medium = "cpc"; }
    else if (q.get("fbclid")) { source = "facebook"; medium = medium || "social"; }
    else if (external) {
      const h = external.hostname.replace(/^www\./, "");
      const eng = ENGINES.find(([rx]) => rx.test(h));
      source = eng ? eng[1] : h;
      medium = eng ? "organic" : SOCIAL.test(h) ? "social" : AI.test(h) ? "ai" : "referral";
    } else { source = "(direct)"; medium = "(none)"; }
  }
  const a = { source: source.toLowerCase().slice(0, 80), medium: (medium || "(none)").toLowerCase().slice(0, 40), campaign: (q.get("utm_campaign") || "").slice(0, 80), term: (q.get("utm_term") || "").slice(0, 80), content: (q.get("utm_content") || "").slice(0, 80), landing: window.location.pathname.toLowerCase().replace(/\/+$/, "") || "/", referrer: external ? external.hostname : "", at: new Date().toISOString() };
  try { sessionStorage.setItem(ATTR, JSON.stringify(a)); } catch { /* private mode */ }
  return a;
}
if (typeof window !== "undefined") attribution();

/* Guard so an action is counted once per browser session (e.g. a payment confirmation page that is reloaded). */
const ONCE = "vl-conv-once";
const once = (key) => {
  try {
    const seen = JSON.parse(sessionStorage.getItem(ONCE) || "[]");
    if (seen.includes(key)) return false;
    sessionStorage.setItem(ONCE, JSON.stringify([...seen, key].slice(-200)));
  } catch { /* still count it */ }
  return true;
};

/* One push for both set-ups: GTM reads dataLayer events; the direct gtag.js fallback needs gtag('event'). With no tag ids the events still land in dataLayer for debugging. */
export function push(event, params = {}) {
  window.dataLayer = window.dataLayer || [];
  const t = tags();
  if (!t.gtm && t.ga && typeof window.gtag === "function") window.gtag("event", event, params);
  else window.dataLayer.push({ event, ...params });
}

export function conversion(name, params = {}, { key } = {}) {
  if (!CONVERSIONS.includes(name)) return false;
  if (key && !once(`${name}:${key}`)) return false;
  const a = attribution();
  const clean = Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ""));
  push(name, { ...clean, landing_page: a.landing, traffic_source: a.source, traffic_medium: a.medium, ...(a.campaign ? { campaign: a.campaign } : {}) });
  const body = { name, params: clean, path: window.location.pathname, landing: a.landing, source: a.source, medium: a.medium, campaign: a.campaign, term: a.term, content: a.content, referrer: a.referrer, session_id: sessionId(), value: typeof clean.value === "number" ? clean.value : null, currency: clean.currency || null };
  apiFetch("/api/visit/conversions", { method: "POST", body: JSON.stringify(body), keepalive: true }).catch(() => {});
  return true;
}

/* Consent Mode v2 update after the cookie banner; also wakes Clarity when analytics is allowed. */
export function applyConsent(c) {
  const analytics = c.analytics ? "granted" : "denied";
  const ads = c.analytics ? "granted" : "denied";
  if (typeof window.gtag === "function") window.gtag("consent", "update", { analytics_storage: analytics, ad_storage: ads, ad_user_data: ads, ad_personalization: ads });
  push("vl_consent_update", { consent_analytics: analytics, consent_ads: ads });
  if (c.analytics && typeof window.__vlLoadClarity === "function") window.__vlLoadClarity();
  if (typeof window.clarity === "function") window.clarity("consentv2", { ad_Storage: ads, analytics_Storage: analytics });
}

/* SPA navigations after the first paint: GTM fires GA4 page_view from this event (History Change trigger also works); gtag.js fallback sends it directly. */
export function pageView(path) {
  const p = { page_path: path, page_location: window.location.href, page_title: document.title };
  const t = tags();
  if (!t.gtm && t.ga && typeof window.gtag === "function") window.gtag("event", "page_view", p);
  else { window.dataLayer = window.dataLayer || []; window.dataLayer.push({ event: "vl_page_view", ...p }); }
}
